<!-- Gemini-like conversation structure: each conversation-container holds one user message + AI responses -->
@for (group of this.groupedMessages(); track group.userMessage.id; let last = $last) {
<div class="conversation-container" [class.last]="last">
  <!-- User Query Section -->
  <div class="user-query" [class.stack]="themeService.getChatBubbleStacked()">
    <div class="bubble right">
      @if (group.userMessage.id === '0') {
        <div class="k-typing-indicator"><span></span><span></span><span></span></div>
      } @else {
        @if (group.userMessage.formatted && group.userMessage.formatted !== 'Processing...') {
          @if (isCollapsible(group.userMessage.clipboardVersion)) {
            <details class="bubble-collapse" open>
              <summary [attr.aria-label]="'Toggle message'">
                <span class="collapse-preview" aria-hidden="true">{{ stripPseudoXml(group.userMessage.clipboardVersion ?? '') | truncateParagraph:collapsePreviewWordCount }}</span>
              </summary>
              <div class="content img-wrapper">
                <app-dynamic-markdown [markdownText]="group.userMessage.formatted"></app-dynamic-markdown>
              </div>
            </details>
          } @else {
            <div class="content img-wrapper">
              <app-dynamic-markdown [markdownText]="group.userMessage.formatted"></app-dynamic-markdown>
            </div>
          }

          @if (group.userMessage.meta) {
            <div class="attachments">
              @for (document of group.userMessage.meta.documents; track document) {
              <div class="attachment">
                <attachment-doc [document]="document"></attachment-doc>
              </div>
              }
            </div>
          }
        }

        <!-- User message action buttons -->
        @if ((group.userMessage.formatted && group.userMessage.formatted !== 'Processing...') && group.userMessage.formatted !== 'Welcome to CGI Federal AI Assist. Please use responsibly.') {
          <div class="btn-grp-assistant-resp">
            <button matIconButton type="button" class="btn-chat icon-sm" #tooltip="matTooltip"
                    aria-label="Quick add prompt to Automation Library" matTooltip="Quick add prompt to Automation Library"
                    matTooltipPosition="above" (click)="quickAddPromptToPromptLibrary(group.userMessage.clipboardVersion!)">
              <mat-icon class="material-symbols-outlined" fontIcon="save" />
            </button>
            <button matIconButton type="button" class="btn-chat icon-sm" #tooltip="matTooltip" aria-label="Copy response"
                    matTooltip="Copy response" matTooltipPosition="above"
                    (click)="copyToClipboard(group.userMessage.clipboardVersion!)">
              <mat-icon class="material-symbols-outlined" fontIcon="content_copy" />
            </button>
          </div>
        }
      }
    </div>
  </div>

  <!-- Model Response Section -->
  <div class="model-response" [class.stack]="themeService.getChatBubbleStacked()">
    @for (aiMessage of group.aiMessages; track aiMessage.id) {
    <div class="bubble" [class.system]="aiMessage.role === chatRole.System">
      @if (aiMessage.id === '0') {
        <div class="k-typing-indicator"><span></span><span></span><span></span></div>
      } @else {
        <!-- Display message card -->
        @if (aiMessage.formatted && aiMessage.formatted !== 'Processing...') {
          @if (aiMessage.role === chatRole.System && aiMessage.showExpansionPanel) {
            <!-- Expansion panel for system messages with long content -->
            <details class="system-message-expansion-panel">
              <summary class="system-message-expansion-panel-title">
                <span>{{ aiMessage.expansionPanelTitle }}</span>
              </summary>
              <div class="system-message-expansion-panel-content">
                <app-dynamic-markdown [markdownText]="aiMessage.formatted"></app-dynamic-markdown>
              </div>
              <div class="system-message-expansion-panel-footer">
                <button matIconButton type="button" class="btn-chat icon-sm" [class.sys]="aiMessage.role === chatRole.System"
                        #tooltip="matTooltip" aria-label="Copy response" matTooltip="Copy response" matTooltipPosition="above"
                        (click)="copyToClipboard(aiMessage.clipboardVersion!)">
                  <mat-icon class="material-symbols-outlined" fontIcon="content_copy" />
                </button>
              </div>
            </details>
          } @else {
            @if (isCollapsible(aiMessage.clipboardVersion)) {
              <!-- Collapsible bubble for long messages -->
              <details class="bubble-collapse" open>
                <summary [attr.aria-label]="'Toggle message'">
                  <span class="collapse-preview" aria-hidden="true">{{ stripPseudoXml(aiMessage.clipboardVersion ?? '') | truncateParagraph:collapsePreviewWordCount }}</span>
                </summary>
                <div class="content img-wrapper" [class.streaming-active]="aiMessage.isStreaming">
                  <app-dynamic-markdown [markdownText]="aiMessage.formatted"></app-dynamic-markdown>
                  @if (aiMessage.isStreaming) {
                    <div class="streaming-indicator">
                      <span class="streaming-dot"></span>
                      <span class="streaming-text">Streaming...</span>
                    </div>
                  }
                </div>
              </details>
            } @else {
              <div class="content img-wrapper" [class.streaming-active]="aiMessage.isStreaming">
                <app-dynamic-markdown [markdownText]="aiMessage.formatted"></app-dynamic-markdown>
                @if (aiMessage.isStreaming) {
                  <div class="streaming-indicator">
                    <span class="streaming-dot"></span>
                    <span class="streaming-text">Streaming...</span>
                  </div>
                }
              </div>
            }
          }

          @if (aiMessage.meta) {
            <div class="attachments">
              @for (document of aiMessage.meta.documents; track document) {
              <div class="attachment">
                <attachment-doc [document]="document"></attachment-doc>
              </div>
              }
            </div>
          }
        } @else {
          <!-- Display processing message -->
          <div class="processing">
            @if (automationStateService.isWorkflowInProgress() || automationStateService.isFileUploadInProgress() || automationStateService.instructionInProgress()) {
            <div class="process-notice">
              <span class="txt">Processing</span>
              <mat-progress-bar mode="buffer" class="processing-bar"></mat-progress-bar>
            </div>
            } @else {
            <span class="txt">Processing...</span>
            <div class="workflow-wrapper">
              @if (hasProperty(aiMessage, "progressDetail")) {
              @if (hasProperty(aiMessage.progressDetail, "workflow")) {
              <div [appMermaid]="generateMermaidDiagram(aiMessage.progressDetail.workflow)" class="workflow"></div>
              }
              }
            </div>
            }
          </div>
        }

        <!-- AI message action buttons -->
        @if ((aiMessage.formatted && aiMessage.formatted !== 'Processing...') && aiMessage.formatted !== 'Welcome to CGI Federal AI Assist. Please use responsibly.') {
          <div class="btn-grp-assistant-resp">
            @if (aiMessage.role !== chatRole.User && aiMessage.role !== chatRole.System) {
            <button matIconButton type="button" class="btn-chat icon-sm" #tooltip="matTooltip" (click)="showRequestDetails(aiMessage)"
                    aria-label="Get details of how this answer was generated"
                    matTooltip="Get details of how this answer was generated" matTooltipPosition="above">
              <mat-icon class="material-symbols-outlined" fontIcon="info" />
            </button>
            }
            @if (!aiMessage.showExpansionPanel) {
            <button matIconButton type="button" class="btn-chat icon-sm" [class.sys]="aiMessage.role === chatRole.System"
                    #tooltip="matTooltip" aria-label="Copy response" matTooltip="Copy response" matTooltipPosition="above"
                    (click)="copyToClipboard(aiMessage.clipboardVersion!)">
              <mat-icon class="material-symbols-outlined" fontIcon="content_copy" />
            </button>
            }
          </div>
        }
      }
    </div>
    }
  </div>
</div>
}
